import type { CartLineItem } from "@wbly/data-storefront";
import { useStore } from "@nanostores/react";
import { Icon } from "@wbly/ui";
import {
  iconsaxBook,
  iconsaxEdit,
  iconsaxStar1,
  iconsaxX,
} from "@wbly/tokens/icon";
import { pushEvent } from "@wbly/common";
import type { DataLayer } from "@wbly/common";
import { formatGifteeNameList } from "../../../util/format-children-list";
import * as LightboxDialog from "../LightboxDialog";
import type { CartItemsProps } from "../CartItems";
import {
  cartDetails,
  deleteItem,
} from "../../../pages/[locale]/cart/stores/details.store";
import { interpolateText } from "../../../util/interpolate-text";
import styles from "./CartItem.module.css";
import { getActionLocaleUrl, triggerEvent } from "./utils/CartItem.utils";
import { useMuseImageUrl } from "./useMuseImageUrl";

type CartItemProps = CartLineItem & {
  staticContent: CartItemsProps["staticContent"];
  marketplace: string;
};

export function CartItem({
  solidus,
  prismic,
  staticContent,
  marketplace,
}: CartItemProps) {
  const $cartDetails = useStore(cartDetails);

  const gifteeNameList = formatGifteeNameList(solidus.gifteeNameList, "");
  const thumbnailImageSize = 128;
  const lightboxImageSize = 320;

  let addons: typeof staticContent.addons = [];
  solidus.components.forEach(({ id }) => {
    const parts = id.split(":");
    const type = parts[0];
    const design = parts[parts.length - 1];
    /**
     * Addons data in Prismic does not match the addon id in `components`.
     *
     * The cart component (generally?) goes `[type]:something:[design]`,
     * so if we have a match on `type` and `design` then assume it's what we want.
     *
     * Fails on e.g. WAY `giftwrap:jumbo:wonderblycrowns:v2`, but it doesn't work on website-v2 either.
     */
    const match = staticContent.addons.find((addon) => {
      return type === addon.type && design === addon.design;
    });

    if (match) {
      addons.push(match);
    } else if (parts[0] === "giftwrap") {
      // I think it's better to show `+ Gift wrap` in English
      // than no indication there's a giftwrap at all.
      addons.push({
        type,
        design,
        label: staticContent.general.giftWrap,
      });
    }
  });

  const { imageUrl: museImageUrl, srcSet: museImageSrcSet } = useMuseImageUrl(
    solidus.coverImage,
    prismic?.image.url,
    lightboxImageSize
  );

  const { imageUrl: thumbnailImageUrl, srcSet: thumbnailImageSrcSet } =
    useMuseImageUrl(solidus.coverImage, prismic?.image.url, thumbnailImageSize);
  if (addons.length === 0) {
    addons = null;
  }

  // Pre-compute the title as displayed in cart to re-use in the lightbox
  const title = (
    <>
      <strong className={styles["CartItem-title"]}>
        {prismic ? prismic.title : solidus.productSku.split("-").join(" ")}
      </strong>
      {gifteeNameList && (
        <>
          {" "}
          {staticContent.general.lineItemPrefix}{" "}
          <strong>{gifteeNameList}</strong>
        </>
      )}
    </>
  );

  function handleDeleteItem() {
    deleteItem({ itemId: solidus.id });
    pushEvent(
      window.dataLayer as DataLayer[],
      "CartV3",
      "remove_item",
      `${solidus.productSlug}`
    );
  }

  return (
    <div className={styles.CartItem}>
      <div className={styles["CartItem-imageContainer"]}>
        <div className={styles["CartItem-imageInner"]}>
          <img
            className={styles["CartItem-image"]}
            src={thumbnailImageUrl}
            alt={prismic?.image.alt}
            srcSet={thumbnailImageSrcSet}
          />

          {addons?.[0].image?.url && (
            <img
              className={styles["CartItem-addonImage"]}
              src={addons[0].image.url}
              alt={addons[0].label}
            />
          )}

          <LightboxDialog.Root>
            <LightboxDialog.TriggerButton
              className={styles["CartItem-lightboxTrigger"]}
            />
            <LightboxDialog.Content>
              <LightboxDialog.Image>
                <img
                  src={museImageUrl}
                  alt={prismic?.image.alt}
                  srcSet={museImageSrcSet}
                />
              </LightboxDialog.Image>
            </LightboxDialog.Content>
          </LightboxDialog.Root>
        </div>
      </div>

      <div className={styles["CartItem-titleContainer"]}>
        <p className={styles["CartItem-title"]}>{title}</p>
        {solidus.format && (
          /* (Note content in this element is capitalised in CSS) */
          <p className={styles["CartItem-cover"]}>
            {staticContent.covers[solidus.format.identifier] ??
              // ^ Prefer using the `identifier` over the `id` if available
              staticContent.covers[solidus.format.id] ??
              solidus.customisations?.cover}
            {/* ^ Fall back to the raw cover value if the dictionary is missing an entry */}

            {addons && addons.map(({ label }) => ` + ${label}`)}
          </p>
        )}
      </div>

      <div className={styles["CartItem-priceContainer"]}>
        <p className={styles["CartItem-price"]}>
          {solidus.discountedAmount.formatted}
        </p>

        {solidus.savings && (
          <p className={styles["CartItem-savingsLabel"]}>
            {interpolateText(staticContent.savingsPriceLabel, {
              price: solidus.savings.price.formatted,
            })}
          </p>
        )}
      </div>

      {/* TODO: is this the correct check for edit book actions? */}
      {solidus.type === "pod" && (
        <nav className={styles["CartItem-actions"]}>
          <a
            data-testid="cart-edit-book-link"
            className={styles["CartItem-action"]}
            onClick={() => triggerEvent("preview", solidus.productSlug)}
            href={getActionLocaleUrl({
              marketplace,
              destination: "preview",
              productSlug: solidus.productSlug,
              itemId: solidus.id,
            })}
          >
            <Icon href={iconsaxEdit} size="small" />
            {staticContent.editItemText}
          </a>

          {prismic?.formatsEnabled && (
            <a
              data-testid="cart-edit-format-link"
              className={styles["CartItem-action"]}
              onClick={() => triggerEvent("formats", solidus.productSlug)}
              href={getActionLocaleUrl({
                marketplace,
                destination: "formats",
                productSlug: solidus.productSlug,
                itemId: solidus.id,
              })}
            >
              <Icon href={iconsaxBook} size="small" />
              {staticContent.editFormatText}
            </a>
          )}

          {prismic?.hasAddons && (
            <a
              data-testid="cart-edit-extras-link"
              className={styles["CartItem-action"]}
              onClick={() => triggerEvent("add-ons", solidus.productSlug)}
              href={getActionLocaleUrl({
                marketplace,
                destination: "add-ons",
                productSlug: solidus.productSlug,
                itemId: solidus.id,
              })}
            >
              <Icon href={iconsaxStar1} size="small" />
              {staticContent.editExtrasText}
            </a>
          )}
        </nav>
      )}

      <button
        disabled={$cartDetails.status !== "success"}
        className={styles["CartItem-removeButton"]}
        onClick={() => handleDeleteItem()}
      >
        <Icon href={iconsaxX} />
      </button>
    </div>
  );
}
